// ============================================================================
// req-guard id helpers.
//
// - `formatRequirementId` produces the DISPLAY id (RQ-###). This is UI identity
//   only and must NEVER be used as an API key (see plan §3 invariants).
// - `generateClientId` produces client-generated UUIDs used for threadId /
//   versionId under the client-simulation backend bridge (plan §6, option A).
// ============================================================================

/**
 * Format a zero-based requirement index into its display id, e.g.
 * `formatRequirementId(0) === 'RQ-001'`, `formatRequirementId(41) === 'RQ-042'`.
 *
 * The index is 0-based; the printed number is 1-based and padded to 3 digits.
 * Values ≥ 999 are not truncated (e.g. index 999 → 'RQ-1000').
 */
export function formatRequirementId(index: number): string {
  const oneBased = index + 1
  return `RQ-${String(oneBased).padStart(3, '0')}`
}

/**
 * Generate a client-side UUID for use as a threadId / versionId under the
 * client-simulation bridge. Falls back to a non-crypto id if `crypto.randomUUID`
 * is unavailable (e.g. very old/non-secure contexts).
 */
export function generateClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback: RFC4122-ish v4 without crypto (best-effort, non-secure).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
